<script setup>
import { computed, ref } from "vue";
import { Link, usePage } from "@inertiajs/vue3";
import { useApiService } from "@/services/useApiService";
import { useModalogStore } from "@/stores/modalog";
import BrandButton from "@/Components/Brand/BrandButton.vue";

const props = defineProps({
    user: {
        type: Object,
        required: true,
    },
});

const apiService = useApiService();
const modalogStore = useModalogStore();
const isSubmitting = ref(false);
const menuOpen = ref(false);

/**
 * Decorative banner gradients from the mockup, assigned deterministically per
 * user so a card keeps its colors across searches and pagination.
 */
const bannerGradients = [
    "linear-gradient(-42deg, #eb2eb3 2%, #48057c 112%)",
    "linear-gradient(-42deg, #62d693 2%, #0b5d63 112%)",
    "linear-gradient(-42deg, #ffc397 2%, #f767a8 112%)",
    "linear-gradient(-42deg, #d31448 2%, #2c0850 112%)",
    "linear-gradient(-42deg, #b7e63c 2%, #1e9e4e 112%)",
    "linear-gradient(-42deg, #ff9d2f 2%, #ee2737 112%)",
];

const bannerStyle = computed(() => {
    const seed = [...(props.user.uuid || props.user.username || "")].reduce(
        (sum, char) => sum + char.charCodeAt(0),
        0,
    );
    return { backgroundImage: bannerGradients[seed % bannerGradients.length] };
});

const authUser = computed(() => usePage().props.auth.user?.data);
const isMe = computed(() => props.user.uuid === authUser.value?.uuid);

function unfollowUser() {
    menuOpen.value = false;
    modalogStore.showModalog({
        type: "ModalogUnfollowConfirmation",
        props: {
            user: props.user,
        },
        isConfirmDisabled: false,
    });
}

function blockUnblockUser() {
    menuOpen.value = false;
    modalogStore.showModalog({
        type: "ModalogBlockUnblockUserConfirmation",
        props: {
            action: props.user.is_blocked ? "unblock" : "block",
            isConfirmDisabled: false,
            redirectAfterBlocking: false,
            user: props.user,
        },
    });
}

function followUser() {
    isSubmitting.value = true;

    apiService
        .followUser(props.user.uuid)
        .then(() => {
            props.user.following = true;
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <div
        class="flex flex-col rounded-brand border border-solid border-brand-tan bg-white p-4"
    >
        <!-- Banner: decorative gradient with badge + options menu -->
        <div class="relative h-[70px] rounded-[4px]" :style="bannerStyle">
            <span
                v-if="user.is_gift_hero"
                class="absolute left-2 top-2 grid size-[30px] place-items-center rounded-[2px] bg-black/40"
                title="Top Gift Hero"
            >
                <img
                    :src="asset('images/friends/badge-top-hero.png')"
                    alt="Top Gift Hero"
                    class="size-[15px]"
                />
            </span>
            <span
                v-else-if="user.is_new"
                class="absolute left-2 top-2 grid size-[30px] place-items-center rounded-[2px] bg-white/40"
                title="New to Gift Hero"
            >
                <img
                    :src="asset('images/brand/logo-mark.png')"
                    alt="New to Gift Hero"
                    class="size-[15px] object-contain"
                />
            </span>

            <span
                v-if="!isMe"
                v-click-outside="() => (menuOpen = false)"
                class="absolute right-2 top-2"
            >
                <button
                    type="button"
                    class="grid size-[30px] place-items-center rounded-[2px] bg-white/40 transition hover:bg-white/60"
                    aria-label="More options"
                    @click="menuOpen = !menuOpen"
                >
                    <svg
                        viewBox="0 0 13 2.6"
                        class="h-[3px] w-[13px]"
                        fill="#102a3d"
                        aria-hidden="true"
                    >
                        <circle cx="1.3" cy="1.3" r="1.3" />
                        <circle cx="6.5" cy="1.3" r="1.3" />
                        <circle cx="11.7" cy="1.3" r="1.3" />
                    </svg>
                </button>

                <div
                    v-show="menuOpen"
                    class="absolute right-0 z-20 mt-1 w-52 overflow-hidden rounded-brand bg-white shadow-brand-hero ring-1 ring-black/5"
                >
                    <button
                        v-if="user.following"
                        type="button"
                        class="block w-full px-4 py-3 text-left font-brand text-[14px] text-brand-red transition hover:bg-brand-cream/60"
                        @click="unfollowUser"
                    >
                        Stop Following
                    </button>
                    <button
                        v-if="!user.is_gift_hero"
                        type="button"
                        class="block w-full px-4 py-3 text-left font-brand text-[14px] text-brand-red transition hover:bg-brand-cream/60"
                        @click="blockUnblockUser"
                    >
                        {{ user.is_blocked ? "Unblock" : "Block" }} @{{
                            user.username
                        }}
                    </button>
                </div>
            </span>
        </div>

        <!-- Avatar overlapping the banner -->
        <Link
            :href="route('user.giftlists', { username: user.username })"
            class="relative z-10 -mt-[47px] self-center"
            :title="user.presentation_name"
        >
            <img
                :src="user.avatar.profile_photo_find_friends"
                :alt="user.presentation_name"
                class="size-[93px] rounded-full border-[7px] border-solid border-white object-cover"
            />
        </Link>

        <Link
            :href="route('user.giftlists', { username: user.username })"
            class="mt-1 text-center font-brand text-[22px] font-semibold leading-tight !text-[#102a3d] no-underline"
        >
            {{ user.presentation_name }}
        </Link>

        <!-- Stats bar -->
        <div
            class="mt-3 flex items-center justify-between gap-1 rounded-[2px] border border-solid border-[#e9e8e0] px-3 py-2 font-brand text-[10px] text-[#102a3d]"
        >
            <span class="flex items-center gap-1.5">
                <svg
                    viewBox="0 0 9 8"
                    class="h-2 w-[9px] shrink-0"
                    fill="#f21439"
                    aria-hidden="true"
                >
                    <path
                        d="M8.76 .23A.82 .82 0 0 0 8.18 0H.82A.8 .8 0 0 0 .24 .23 .78 .78 0 0 0 0 .8c0 .23 .08 .42 .23 .57 .16 .15 .36 .23 .59 .23h7.36c.23 0 .43-.08 .58-.23A.76 .76 0 0 0 9 .8 .78 .78 0 0 0 8.76 .23ZM8.76 3.43a.82 .82 0 0 0-.58-.23H.82a.8 .8 0 0 0-.58 .23A.78 .78 0 0 0 0 4c0 .23 .08 .42 .23 .57 .16 .15 .36 .23 .59 .23h7.36c.23 0 .43-.08 .58-.23A.76 .76 0 0 0 9 4a.78 .78 0 0 0-.24-.57ZM8.76 6.63a.82 .82 0 0 0-.58-.23H.82a.8 .8 0 0 0-.58 .23A.78 .78 0 0 0 0 7.2c0 .23 .08 .42 .23 .57 .16 .15 .36 .23 .59 .23h7.36c.23 0 .43-.08 .58-.23A.76 .76 0 0 0 9 7.2a.78 .78 0 0 0-.24-.57Z"
                    />
                </svg>
                <span
                    ><strong class="font-bold">{{
                        user.list_count ?? 0
                    }}</strong>
                    {{ user.list_count === 1 ? "List" : "Lists" }}</span
                >
            </span>

            <span class="flex items-center gap-1.5">
                <svg
                    viewBox="0 0 13.125 9.545"
                    class="h-[10px] w-[13px] shrink-0"
                    fill="#f21439"
                    aria-hidden="true"
                >
                    <path
                        d="M2.39 6.56 2.03 6.26C.84 5.13 0 4.41 0 3.52c0-.72 .6-1.31 1.31-1.31 .42 0 .84 .18 1.07 .48 .24-.3 .66-.48 1.07-.48 .72 0 1.31 .54 1.31 1.31 0 .9-.84 1.61-2.03 2.74l-.36 .3ZM8.35 0a2.39 2.39 0 1 0 0 4.77 2.39 2.39 0 0 0 0-4.77Zm0 3.64a1.25 1.25 0 1 1 0-2.51 1.25 1.25 0 0 1 0 2.51Zm0 1.73c-1.59 0-4.77 .79-4.77 2.39v1.79h9.55V7.76c0-1.59-3.18-2.39-4.77-2.39Zm3.64 3.04H4.71v-.65c0-.38 1.85-1.25 3.64-1.25 1.77 0 3.64 .87 3.64 1.25v.65Z"
                    />
                </svg>
                <span
                    ><strong class="font-bold">{{
                        user.followers_count ?? 0
                    }}</strong>
                    Followers</span
                >
            </span>

            <span class="flex items-center gap-1.5">
                <svg
                    viewBox="0 0 12.158 11"
                    class="h-[11px] w-3 shrink-0"
                    fill="#f21439"
                    aria-hidden="true"
                >
                    <path
                        d="M4.63 4.63a2.32 2.32 0 1 0 0-4.63 2.32 2.32 0 0 0 0 4.63Zm0-3.47a1.16 1.16 0 1 1 0 2.32 1.16 1.16 0 0 1 0-2.32ZM4.21 9.26H0V7.53c0-1.55 3.09-2.32 4.63-2.32 .6 0 1.45 .12 2.23 .35-.5 .2-.96 .47-1.37 .81a7.5 7.5 0 0 0-.87-.05c-1.72 0-3.53 .85-3.53 1.21v.64h3.08l-.05 .09-.17 .43 .17 .43 .09 .14ZM8.68 8.11a.58 .58 0 1 1 0 1.16 .58 .58 0 0 1 0-1.16Zm0-1.74a3.75 3.75 0 0 0-3.47 2.32A3.75 3.75 0 0 0 8.68 11a3.75 3.75 0 0 0 3.47-2.32 3.75 3.75 0 0 0-3.47-2.32Zm0 3.76a1.45 1.45 0 1 1 0-2.9 1.45 1.45 0 0 1 0 2.9Z"
                    />
                </svg>
                <span
                    ><strong class="font-bold">{{
                        user.following_count ?? 0
                    }}</strong>
                    Following</span
                >
            </span>
        </div>

        <p class="mt-3 text-center font-brand text-[14px] text-[#565a77]">
            @{{ user.username }}
        </p>

        <!-- Follow state -->
        <div class="mt-auto pt-4">
            <BrandButton
                v-if="isMe"
                class="pointer-events-none h-11 w-full text-[15px] opacity-60"
                disabled
            >
                That&rsquo;s Me!
            </BrandButton>
            <div
                v-else-if="user.following"
                class="grid h-11 w-full place-items-center rounded-brand border border-solid border-brand-tan font-brand text-[15px] font-bold text-brand-navy"
            >
                <span class="flex items-center gap-2">
                    <svg
                        viewBox="0 0 16 16"
                        class="size-4 text-[#008844]"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M2.5 8.5 6.5 12.5 13.5 4" />
                    </svg>
                    Following
                </span>
            </div>
            <BrandButton
                v-else
                class="h-11 w-full text-[15px]"
                :class="{ 'pointer-events-none opacity-60': isSubmitting }"
                :disabled="isSubmitting"
                @click="followUser"
            >
                {{ isSubmitting ? "Following…" : "Follow" }}
            </BrandButton>
        </div>
    </div>
</template>
